// feat-0923-broken-media-alerts client reporter (SPEC.md D1: detection = BOTH a
// nightly server sweep AND instant client reports). This is the client half:
// wherever ASSIGNED media (Segment Fill / Image or media Block / Wallpaper /
// Wheel Profile / Avatar) fails to load in View Wheel, Edit Wheel or an avatar
// surface, report it once per url per session. The server re-verifies the url
// (HEAD/GET, 5s timeout) before recording anything, so a stale/transient
// client-side failure can never poison the record on its own -- this module's
// only job is dedupe + never blocking the caller's render path.
//
// Session-scoped (sessionStorage, not localStorage): a url that comes back
// broken should be reportable again in a FRESH tab/session rather than being
// silently suppressed forever by a stale local record.
import { BROKEN_MEDIA_ALERTS_ENABLED } from "../constants/featureFlags";
import { reportBrokenMedia } from "../services/api";

const SESSION_KEY = "brokenMediaReported:v1";

function readReportedSet() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return new Set();
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function persistReportedSet(set) {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* best-effort only -- a full/blocked sessionStorage must never break the report */
  }
}

/**
 * Report a broken-media load failure, deduped per url for this browser
 * session. Never throws and never returns a value the caller needs to await --
 * fire-and-forget by design.
 *
 * @param {{ wheelId?: (string|number), kind: string, segmentId?: (string|number), blockId?: (string|number), url: string }} report
 */
export function reportBrokenMediaOnce(report) {
  if (!BROKEN_MEDIA_ALERTS_ENABLED) return;
  const url = report && report.url;
  if (!url) return;
  const reported = readReportedSet();
  if (reported.has(url)) return;
  reported.add(url);
  persistReportedSet(reported);
  try {
    reportBrokenMedia(report);
  } catch {
    /* fire-and-forget: never let a reporting failure surface to the caller */
  }
}

/** Test-only escape hatch so specs don't leak dedupe state across cases. */
export function __resetBrokenMediaReportedForTests() {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* noop */
  }
}
